import type { HTMLAttributes } from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  type FacehashProps,
} from 'facehash';

import {
  buildDefaultProfileImageColor,
  buildDefaultProfileImageName,
  isLegacyFacehashProfileImageUrl,
  type FacehashProfileImageInput,
} from '../../utils/profileImage';
import { useStableImageSource } from '../../utils/stableImageSource';

export interface ProfileAvatarProps
  extends HTMLAttributes<HTMLSpanElement>, FacehashProfileImageInput {
  alt?: string;
  src?: string | null;
  facehashProps?: Omit<FacehashProps, 'name'>;
}

export function ProfileAvatar({
  actorId,
  alt,
  className,
  displayName,
  email,
  facehashProps,
  firstName,
  lastName,
  fallbackSeed,
  src,
  ...props
}: ProfileAvatarProps) {
  const fallbackName = buildDefaultProfileImageName({
    actorId,
    displayName,
    email,
    firstName,
    lastName,
    fallbackSeed,
  });
  const fallbackColor = buildDefaultProfileImageColor({
    actorId,
    displayName,
    email,
    firstName,
    lastName,
    fallbackSeed,
  });
  const resolvedImageSource =
    src && !isLegacyFacehashProfileImageUrl(src) ? src : null;
  const stableImageSource = useStableImageSource(resolvedImageSource);

  return (
    <Avatar aria-label={alt} className={className} {...props}>
      <AvatarImage alt={alt ?? ''} src={stableImageSource} />
      <AvatarFallback
        name={fallbackName}
        facehashProps={{
          interactive: false,
          colors: [fallbackColor],
          intensity3d: 'none',
          variant: 'solid',
          ...facehashProps,
        }}
      />
    </Avatar>
  );
}
